import React from "react";
import ServicePage from "../components/ServicePage";

// ─────────────────────────────────────────────────────────────────────────────
// Local-SEO landing pages for Huntsville, Alabama.
// Copy is deliberately factual — NO fabricated certifications, response-time
// guarantees, licenses or awards. Any claim that appears must already be
// present on the homepage or in the business info.
// ─────────────────────────────────────────────────────────────────────────────

export function EmergencyTreeServicePage() {
  return (
    <ServicePage
      slug="/emergency-tree-service-huntsville-al"
      title="Emergency Tree Service Huntsville AL"
      description="24/7 emergency tree service in Huntsville, Alabama. Fallen trees, storm damage and dangerous trees threatening homes — call Top of the Line Tree Services LLC any time."
      h1="Emergency Tree Service in Huntsville, AL"
      serviceName="Emergency Tree Service"
      intro="When a tree falls on your house, blocks your driveway, or looks like it&rsquo;s about to come down, you need help right now — not next week. Top of the Line Tree Services LLC responds to emergency tree calls across Huntsville and North Alabama, day or night."
      bullets={[
        "Fallen tree removal from homes, garages and vehicles",
        "Trees leaning dangerously toward structures",
        "Storm damage clean-up after severe weather",
        "Trees blocking driveways, streets or utility access",
        "Large limbs hanging over roofs or power lines",
        "Property clearance so contractors and insurance can access damage",
      ]}
      body={[
        {
          heading: "When to call for emergency tree service",
          text:
            "Not every fallen limb is an emergency, but some tree situations cannot wait. If a tree has already fallen on your house or another structure, has split at the trunk, is uprooted and leaning, or is resting on power lines, treat it as a genuine emergency and call us immediately.\n\nOther situations that usually justify an emergency call: a large tree with visible cracks near the base after a windstorm, a tree that shifted position noticeably after heavy rain, or any tree in your yard whose failure would put people, pets or property at risk before we could schedule a normal appointment.",
        },
        {
          heading: "Storm damage and what happens next",
          text:
            "Severe storms across Huntsville, Madison and the rest of North Alabama regularly bring down mature oaks, pines and hickories — sometimes onto roofs, cars or fences. Our crew arrives with the climbing gear, chainsaws and rigging needed to remove downed trees safely without adding damage to whatever they landed on.\n\nWe can also provide documentation and photos that homeowners and property managers commonly need for insurance claims. We do not handle insurance paperwork on your behalf, but we can make the physical damage well-documented before we start cutting.",
        },
        {
          heading: "How to stay safe until we arrive",
          text:
            "If a tree is on your house, leave the affected room and stay outside if it&rsquo;s safe to do so. Never touch a tree that is resting on or near a power line — call the utility company first so they can de-energize the line. Keep children and pets away from any tree that is leaning or has a visible root plate lifted out of the ground.\n\nOnce our crew is on site we will assess the situation, explain what needs to happen, and get to work. Call (256) 375-8064 any time — including nights and weekends — for tree emergencies in Huntsville, AL and the surrounding area.",
        },
      ]}
      relatedLinks={[
        { to: "/tree-removal-huntsville-al", label: "Tree Removal" },
        { to: "/storm-damage-tree-removal-huntsville-al", label: "Storm Damage Removal" },
        { to: "/tree-trimming-huntsville-al", label: "Tree Trimming" },
        { to: "/blog/5-signs-a-tree-might-fall", label: "5 Signs a Tree Might Fall" },
      ]}
    />
  );
}

export function TreeRemovalPage() {
  return (
    <ServicePage
      slug="/tree-removal-huntsville-al"
      title="Tree Removal Huntsville AL"
      description="Professional tree removal in Huntsville, Alabama. Safe removal of dead, damaged, dangerous or unwanted trees on residential and commercial properties. Free estimates."
      h1="Tree Removal in Huntsville, AL"
      serviceName="Tree Removal"
      intro="Removing a mature tree is not a DIY job. A single wrong cut can drop several tons of wood in the wrong place. Top of the Line Tree Services LLC removes trees of every size across Huntsville and North Alabama — cleanly, safely and with full clean-up when the job is done."
      bullets={[
        "Removal of dead, dying or diseased trees",
        "Trees too close to homes, fences or driveways",
        "Trees damaged by storms or lightning",
        "Species that are dropping heavy limbs or leaning",
        "Trees interfering with construction or landscaping",
        "Complete debris clean-up and haul-away",
      ]}
      body={[
        {
          heading: "Reasons homeowners in Huntsville choose to remove a tree",
          text:
            "Trees are worth keeping when they&rsquo;re healthy and well placed — but not every tree fits either description. The most common reasons customers in Huntsville, Madison, Harvest and Meridianville ask us to remove a tree are: it&rsquo;s dead and starting to drop limbs, it&rsquo;s leaning toward the house, roots are damaging a foundation or driveway, or it&rsquo;s simply the wrong species growing in the wrong spot.\n\nBefore removal we always walk the property with the owner. If the tree can be saved with pruning, cabling or a crown reduction instead, we&rsquo;ll tell you.",
        },
        {
          heading: "How we take a tree down safely",
          text:
            "For trees with plenty of open space around them, we can drop the whole tree in one controlled fall. For trees in tight spots — over roofs, near power lines, or inside fenced-in yards — we climb the tree and take it down in sections, lowering each piece with ropes. This takes longer but eliminates the risk of damaging anything below.\n\nOnce the tree is on the ground we cut it into manageable rounds, chip the brush, and haul everything away. If you want to keep the wood for firewood we can cut it to length and stack it before we leave.",
        },
        {
          heading: "Stump grinding and after-care",
          text:
            "Removing a tree leaves a stump. Most customers want the stump ground down below grade so grass can grow over it and mowers can pass without catching. We offer stump grinding as part of most removal jobs — ask for it up front and we&rsquo;ll include it in your estimate.\n\nWe can also recommend replacement plantings that fit your yard, sun exposure and North Alabama&rsquo;s climate. Every removal is followed by a full property sweep for wood chips, sawdust and small debris.",
        },
      ]}
      relatedLinks={[
        { to: "/emergency-tree-service-huntsville-al", label: "Emergency Tree Service" },
        { to: "/storm-damage-tree-removal-huntsville-al", label: "Storm Damage Removal" },
        { to: "/tree-trimming-huntsville-al", label: "Tree Trimming" },
        { to: "/blog/5-signs-a-tree-might-fall", label: "5 Signs a Tree Might Fall" },
      ]}
    />
  );
}

export function StormDamageTreeRemovalPage() {
  return (
    <ServicePage
      slug="/storm-damage-tree-removal-huntsville-al"
      title="Storm Damage Tree Removal Huntsville AL"
      description="Storm damage tree removal in Huntsville, Alabama. Fast, safe removal of trees, limbs and debris after severe weather — including trees on homes, vehicles and fences."
      h1="Storm Damage Tree Removal in Huntsville, AL"
      serviceName="Storm Damage Tree Removal"
      intro="North Alabama storms — from spring tornadoes to summer thunderstorms and winter ice — regularly send mature trees down onto homes, cars, fences and driveways across Huntsville. Top of the Line Tree Services LLC handles storm-damage tree work as a priority and can usually be on site the same day."
      bullets={[
        "Trees on houses, roofs and garages",
        "Trees on vehicles, sheds and outbuildings",
        "Broken limbs hanging in the canopy (widow-makers)",
        "Uprooted trees with lifted root plates",
        "Split or partially fallen trees threatening property",
        "Full debris removal and property clean-up",
      ]}
      body={[
        {
          heading: "The first hours after a storm",
          text:
            "The first thing to check after severe weather is whether anyone is hurt and whether the damage involves a power line. Never touch a downed tree that is on or near a power line — call the utility company first. Once the area is safe, take pictures of everything before it&rsquo;s moved. Insurance adjusters and contractors will thank you.\n\nThen call us. Storm calls in Huntsville usually come in waves — a big system will bring dozens of trees down across the county at once — so calling early gets you on the schedule sooner.",
        },
        {
          heading: "How storm-damage removals are different",
          text:
            "A tree that failed in a storm is under tension in ways that a healthy standing tree is not. A limb that looks harmless can be pinned between the roof and the trunk with hundreds of pounds of stored force; one wrong cut releases it violently. This is a common cause of injuries for people who try to clean up storm damage themselves.\n\nOur crew is trained to read those tensions and cut in the right order so the tree comes apart in controlled pieces. We rig heavy sections down with ropes and use cranes on the largest jobs so nothing lands where it isn&rsquo;t supposed to.",
        },
        {
          heading: "Documentation, clean-up and next steps",
          text:
            "We can photograph the site as we find it and again after the tree is off the structure, which is often what homeowners insurance asks for. We do not act as insurance agents or estimate roof/structural damage — that&rsquo;s a job for licensed contractors — but our part of the job (the tree) will be fully documented.\n\nWhen the removal is done we haul away the wood and chip the brush. If a stump was left behind we can grind it down on the same visit or come back on a scheduled day.",
        },
      ]}
      relatedLinks={[
        { to: "/emergency-tree-service-huntsville-al", label: "Emergency Tree Service" },
        { to: "/tree-removal-huntsville-al", label: "Tree Removal" },
        { to: "/tree-trimming-huntsville-al", label: "Tree Trimming" },
        { to: "/blog/best-time-to-prune-a-tree", label: "Best Time to Prune a Tree" },
      ]}
    />
  );
}

export function TreeTrimmingPage() {
  return (
    <ServicePage
      slug="/tree-trimming-huntsville-al"
      title="Tree Trimming & Pruning Huntsville AL"
      description="Tree trimming and pruning in Huntsville, Alabama. Crown thinning, deadwooding, clearance pruning and shape work for healthy, safe trees on residential and commercial properties."
      h1="Tree Trimming & Pruning in Huntsville, AL"
      serviceName="Tree Trimming"
      intro="A well-trimmed tree lives longer, drops fewer branches on your roof and looks the way it&rsquo;s supposed to. Top of the Line Tree Services LLC handles trimming and pruning across Huntsville and North Alabama — from small ornamentals in front yards to mature hardwoods over driveways and homes."
      bullets={[
        "Crown thinning to reduce wind resistance",
        "Deadwooding — removing dead limbs before they fall",
        "Clearance pruning away from roofs, gutters and driveways",
        "Shape pruning for ornamentals and specimen trees",
        "Elevation pruning to raise the canopy",
        "Cabling and bracing options for splitting limbs",
      ]}
      body={[
        {
          heading: "Why trimming matters more than most people realize",
          text:
            "Regular pruning is the single biggest thing a homeowner can do to keep a mature tree healthy and to keep it from becoming a hazard. Dead limbs get heavier and more brittle every year they&rsquo;re left up there. Overgrown crowns catch wind like sails, which is what actually breaks trees in storms.\n\nA good trim removes the wood that shouldn&rsquo;t be there — dead, crossing, rubbing or structurally weak limbs — and leaves the healthy scaffolding branches to keep growing. Done right, it&rsquo;s barely visible from the street but adds years to the tree&rsquo;s life.",
        },
        {
          heading: "When is the best time to trim?",
          text:
            "For most species in North Alabama, late winter to very early spring is the ideal window — the tree is dormant, sap flow is minimal, and the shape is easy to read without leaves in the way. Oaks in particular should be pruned in dormancy to reduce the risk of oak wilt.\n\nThat said, dead, dangerous, or broken limbs should come off any time of year, as soon as they&rsquo;re noticed. Light shaping and clearance work can also be done through summer. If you&rsquo;re not sure whether now is the right time for your tree, we&rsquo;ll tell you honestly during the estimate.",
        },
        {
          heading: "What we won&rsquo;t do",
          text:
            "We do not top trees. Topping — cutting the entire crown flat — is one of the fastest ways to kill a large tree and to create the exact structural weakness that causes major failures later. If someone has already topped your tree, we can talk about whether the damage can be gradually corrected through restoration pruning.\n\nWe also don&rsquo;t &ldquo;over-prune&rdquo; to justify a bigger bill. The industry rule of thumb is to remove no more than 25% of a mature tree&rsquo;s canopy in a single visit — usually much less — and we stick to that.",
        },
      ]}
      relatedLinks={[
        { to: "/tree-removal-huntsville-al", label: "Tree Removal" },
        { to: "/emergency-tree-service-huntsville-al", label: "Emergency Tree Service" },
        { to: "/storm-damage-tree-removal-huntsville-al", label: "Storm Damage Removal" },
        { to: "/blog/best-time-to-prune-a-tree", label: "Best Time to Prune a Tree" },
      ]}
    />
  );
}
